import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Plus, X, Copy, Check, Send, Trash2, ArrowDownAZ, ArrowUpAZ, Users, ChevronDown } from "lucide-react";
import PushToggle from "../components/PushToggle";
import SharePortalCard from "../components/SharePortalCard";

/* Rough is fine here: the point of this line is to tell a client who went
   quiet in the spring from one you spoke to yesterday, not to be a clock. */
function sinceLabel(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months <= 1 ? "last month" : `${months} months ago`;
}

/* Showing dates arrive as plain YYYY-MM-DD. Handing that straight to
   new Date() reads it as UTC, which lands a day early for anyone west of
   Greenwich - so build it from the parts in local time instead. */
function dayLabel(ymd) {
  if (!ymd) return "";
  const parts = String(ymd).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !n)) return "";
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });
}

/* The same two lines for both lists. There are two: a table on desktop and
   cards on a phone, and a change to one of them alone is a change nobody
   on the other ever sees. */
function ClientPulse({ s, idPrefix }) {
  return (
    <>
      {s.next_event ? (
        <div data-testid={`next-event-${idPrefix}${s._id}`} style={{ fontSize: 12, marginTop: 6 }}>
          Next: {dayLabel(s.next_event.date)}
          {s.next_event.start_time ? ` at ${s.next_event.start_time}` : ""}
        </div>
      ) : null}
      <div
        data-testid={`last-activity-${idPrefix}${s._id}`}
        style={{ fontSize: 12, marginTop: 4, color: "var(--text-muted)" }}
      >
        {s.last_activity_at
          ? `Last activity ${sinceLabel(s.last_activity_at)}`
          : "No activity yet"}
      </div>
    </>
  );
}

// Listing lifecycle states an agent can flip a seller through.
// `pre_listing` is the default for new sellers; `closed` is the terminal state.
const LIFECYCLE = [
  { id: "pre_listing",    label: "Pre-listing",    color: "#B85C47" },
  { id: "active",         label: "Active",         color: "#2B7A4B" },
  { id: "under_contract", label: "Under contract", color: "#5C4FAA" },
  { id: "closed",         label: "Closed",         color: "#78716C" },
];
const lifecycleLabel = (id) => (LIFECYCLE.find((l) => l.id === id) || LIFECYCLE[1]);

export default function AdminSellers() {
  const { user } = useAuth();
  const buyerFeaturesEnabled = !!user?.buyer_features_enabled;
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | one of LIFECYCLE ids
  const [typeFilter, setTypeFilter] = useState("all");     // 'all' | 'sell' | 'buy' | 'both'
  const [confirmDelete, setConfirmDelete] = useState(null); // seller object

  const load = async () => {
    try { const { data } = await api.get("/admin/sellers"); setSellers(data.sellers || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Lifecycle counts → for the filter chips ("Active 12", "Closed 4")
  const lifecycleCounts = useMemo(() => {
    const counts = { all: sellers.length };
    for (const l of LIFECYCLE) counts[l.id] = 0;
    for (const s of sellers) {
      const k = s.lifecycle_status || "active";
      counts[k] = (counts[k] || 0) + 1;
    }
    return counts;
  }, [sellers]);

  // Client-type counts → drives the Buyer/Seller/Both filter chips. A
  // client's "type" is derived from the sides they have linked: exactly
  // ["sell"] = seller, exactly ["buy"] = buyer, both linked = both.
  const clientType = (s) => {
    const sides = Array.isArray(s.sides) ? s.sides : ["sell"];
    if (sides.length > 1) return "both";
    if (sides[0] === "buy") return "buy";
    return "sell";
  };
  const typeCounts = useMemo(() => {
    const counts = { all: sellers.length, sell: 0, buy: 0, both: 0 };
    for (const s of sellers) counts[clientType(s)]++;
    return counts;
  }, [sellers]);

  const sorted = useMemo(() => {
    const arr = sellers.filter((s) => {
      if (statusFilter !== "all" && (s.lifecycle_status || "active") !== statusFilter) return false;
      if (typeFilter !== "all" && clientType(s) !== typeFilter) return false;
      return true;
    });
    arr.sort((a, b) => {
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      const cmp = an.localeCompare(bn);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [sellers, sortDir, statusFilter, typeFilter]);

  const onCreated = () => {
    setShowAdd(false);
    load();
    setToast("Client invited. You can now personalize their dashboard.");
    setTimeout(() => setToast(""), 3500);
  };

  const resend = async (s) => {
    try {
      const { data } = await api.post(`/admin/sellers/${s._id}/resend-invite`);
      const emailInfo = data.email?.sent ? "Invite email resent." : "Invite refreshed (email skipped).";
      setToast(`${emailInfo} Link: ${data.invite_link}`);
      setTimeout(() => setToast(""), 6000);
      load();
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Resend failed");
      setTimeout(() => setToast(""), 3500);
    }
  };

  const doDelete = async (id, cascade) => {
    try {
      const { data } = await api.delete(`/admin/sellers/${id}`);
      setConfirmDelete(null);
      load();
      setToast(data.cascade
        ? "Seller deleted along with their data."
        : "Seller deleted. Co-owner still has access to the household.");
      setTimeout(() => setToast(""), 4000);
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Delete failed");
      setTimeout(() => setToast(""), 3500);
    }
  };

  const setLifecycle = async (sellerId, value) => {
    // Optimistic — flip the badge instantly, revert on error
    const prev = sellers;
    setSellers((rows) => rows.map((r) => (r._id === sellerId ? { ...r, lifecycle_status: value } : r)));
    try {
      await api.patch(`/admin/sellers/${sellerId}/lifecycle-status`, { lifecycle_status: value });
      setToast(`Status updated to ${lifecycleLabel(value).label}.`);
      setTimeout(() => setToast(""), 2200);
    } catch (e) {
      setSellers(prev);
      setToast(formatApiError(e.response?.data?.detail) || "Failed to update status.");
      setTimeout(() => setToast(""), 3500);
    }
  };

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="overline">Admin · Clients</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 0", letterSpacing: "-0.01em" }}>
            Your clients
          </h1>
          <p style={{ color: "var(--text-muted)", marginTop: 8 }}>Everyone you&rsquo;re guiding through their transaction.</p>
        </div>
        <button data-testid="add-seller-btn" className="btn-primary" onClick={() => setShowAdd(true)}>
          <Plus size={14} /> Add client
        </button>
      </div>

      {toast && <div data-testid="admin-sellers-toast" style={{ marginTop: 16, padding: "12px 16px", borderRadius: 12, background: "#E7F0EA", color: "var(--success)", fontSize: 14 }}>{toast}</div>}

      <div style={{ marginTop: 20 }}>
        <PushToggle />
      </div>

      <SharePortalCard />

      {/* Lifecycle filter chips */}
      <div data-testid="lifecycle-filter" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 24, flexWrap: "wrap" }}>
        <span className="overline" style={{ marginRight: 4 }}>Filter</span>
        <FilterChip
          active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
          color="var(--text-muted)"
          label="All"
          count={lifecycleCounts.all}
          testid="filter-chip-all"
        />
        {LIFECYCLE.map((l) => (
          <FilterChip
            key={l.id}
            active={statusFilter === l.id}
            onClick={() => setStatusFilter(l.id)}
            color={l.color}
            label={l.label}
            count={lifecycleCounts[l.id] || 0}
            testid={`filter-chip-${l.id}`}
          />
        ))}
      </div>

      {/* Client-type filter — Buyer / Seller / Both. Hidden entirely on
          non-beta tenants since they can't have buy or both clients yet. */}
      {buyerFeaturesEnabled && (typeCounts.buy + typeCounts.both > 0) && (
        <div data-testid="type-filter" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <span className="overline" style={{ marginRight: 4 }}>Type</span>
          <FilterChip
            active={typeFilter === "all"}
            onClick={() => setTypeFilter("all")}
            color="var(--text-muted)"
            label="All"
            count={typeCounts.all}
            testid="type-filter-all"
          />
          <FilterChip
            active={typeFilter === "sell"}
            onClick={() => setTypeFilter("sell")}
            color="#2B4C3F"
            label="Sellers"
            count={typeCounts.sell}
            testid="type-filter-sell"
          />
          <FilterChip
            active={typeFilter === "buy"}
            onClick={() => setTypeFilter("buy")}
            color="#B85C47"
            label="Buyers"
            count={typeCounts.buy}
            testid="type-filter-buy"
          />
          <FilterChip
            active={typeFilter === "both"}
            onClick={() => setTypeFilter("both")}
            color="#8B7355"
            label="Both"
            count={typeCounts.both}
            testid="type-filter-both"
          />
        </div>
      )}

      {/* Sort control */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <span className="overline">Sort by name</span>
        <button
          data-testid="sort-asc-btn"
          onClick={() => setSortDir("asc")}
          className={sortDir === "asc" ? "btn-secondary" : "btn-ghost"}
          style={{ padding: "6px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <ArrowDownAZ size={14} /> A → Z
        </button>
        <button
          data-testid="sort-desc-btn"
          onClick={() => setSortDir("desc")}
          className={sortDir === "desc" ? "btn-secondary" : "btn-ghost"}
          style={{ padding: "6px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <ArrowUpAZ size={14} /> Z → A
        </button>
      </div>

      <div className="card" style={{ padding: 0, marginTop: 16, overflow: "hidden" }} data-testid="sellers-table">
        {loading ? (
          <div style={{ padding: 24, color: "var(--text-muted)" }}>Loading…</div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: 24, color: "var(--text-muted)" }}>No clients yet. Click <b>Add client</b> above to invite your first one.</div>
        ) : (
          <>
            <div className="sellers-desktop">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--surface-alt)" }}>
                    {["Name", "Email", "Property", "Status", "Submissions", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "12px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s) => {
                    const pending = s.status === "pending";
                    const co = (s.co_owners || [])[0];
                    // Send buy-only clients into their personalization page
                    // pre-scoped to the buy side so admins don't accidentally
                    // land on sell-side tabs that don't apply to a buyer.
                    const isBuyOnly = Array.isArray(s.sides) && s.sides.length === 1 && s.sides[0] === "buy";
                    const isBoth = Array.isArray(s.sides) && s.sides.length > 1;
                    const personalizeUrl = `/admin/seller/${s._id}${isBuyOnly ? "?side=buy" : (isBoth && s.active_side === "buy" ? "?side=buy" : "")}`;
                    const typeLabel = isBuyOnly ? "Buyer" : (isBoth ? "Buying + Selling" : null);
                    return (
                      <tr key={s._id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`seller-row-${s._id}`}>
                        <td style={{ padding: "14px 20px", fontWeight: 500 }}>
                          {s.name}
                          {co && (
                            <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 400, marginLeft: 6 }}>
                              &amp; {co.name || co.email}
                            </span>
                          )}
                          {typeLabel && (
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {typeLabel}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "14px 20px", color: "var(--text-muted)" }}>
                          {s.email}
                          {co && (
                            <div style={{ fontSize: 12, marginTop: 2 }}>{co.email}</div>
                          )}
                        </td>
                        <td style={{ padding: "14px 20px", color: "var(--text-muted)" }}>{s.property_address || "—"}</td>
                        <td style={{ padding: "14px 20px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {pending && <span className="badge badge-warning">Invite sent</span>}
                            <LifecyclePicker
                              sellerId={s._id}
                              value={s.lifecycle_status || "active"}
                              onChange={(v) => setLifecycle(s._id, v)}
                            />
                          </div>
                        </td>
                        <td style={{ padding: "14px 20px" }}>
                          <span className={`badge ${s.submission_count > 0 ? "badge-success" : ""}`}>{s.submission_count} submitted</span>
              <ClientPulse s={s} idPrefix="" />
                        </td>
                        <td style={{ padding: "14px 20px", display: "flex", gap: 12, whiteSpace: "nowrap", alignItems: "center" }}>
                          <Link to={personalizeUrl} data-testid={`view-seller-${s._id}`} style={{ color: "var(--primary)", fontWeight: 500 }}>Open workspace</Link>
                          {pending && (
                            <button onClick={() => resend(s)} data-testid={`resend-invite-${s._id}`} className="btn-ghost" style={{ padding: 0, fontSize: 13, color: "var(--secondary)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <Send size={12} /> Resend
                            </button>
                          )}
                          <button
                            data-testid={`delete-seller-${s._id}`}
                            onClick={() => setConfirmDelete(s)}
                            className="btn-ghost"
                            title="Delete client"
                            style={{ padding: 0, color: "var(--error, #B85C47)", display: "inline-flex" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="sellers-mobile" data-testid="sellers-mobile-list">
              {sorted.map((s) => {
                const pending = s.status === "pending";
                const co = (s.co_owners || [])[0];
                const isBuyOnly = Array.isArray(s.sides) && s.sides.length === 1 && s.sides[0] === "buy";
                const isBoth = Array.isArray(s.sides) && s.sides.length > 1;
                const personalizeUrl = `/admin/seller/${s._id}${isBuyOnly ? "?side=buy" : (isBoth && s.active_side === "buy" ? "?side=buy" : "")}`;
                const typeLabel = isBuyOnly ? "Buyer" : (isBoth ? "Buying + Selling" : null);
                return (
                  <div key={s._id} data-testid={`seller-card-${s._id}`} style={{ padding: "18px 20px", borderTop: "1px solid var(--border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.3 }}>
                          {s.name}
                          {co && <span style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 400 }}> &amp; {co.name || co.email}</span>}
                        </div>
                        {typeLabel && (
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>{typeLabel}</div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                        {pending && <span className="badge badge-warning">Invite sent</span>}
                        <LifecyclePicker
                          sellerId={s._id}
                          value={s.lifecycle_status || "active"}
                          onChange={(v) => setLifecycle(s._id, v)}
                          compact
                        />
                      </div>
                    </div>
                    <div style={{ color: "var(--text-muted)", fontSize: 14, wordBreak: "break-word" }}>
                      {s.email}
                      {co && <div style={{ fontSize: 13, marginTop: 2 }}>{co.email}</div>}
                    </div>
                    {s.property_address && <div style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>{s.property_address}</div>}
                    <div style={{ marginTop: 10 }}>
                      <span className={`badge ${s.submission_count > 0 ? "badge-success" : ""}`}>{s.submission_count} submitted</span>
                <ClientPulse s={s} idPrefix="card-" />
                    </div>
                    <div style={{ display: "flex", gap: 16, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
                      <Link to={personalizeUrl} data-testid={`view-seller-card-${s._id}`} style={{ color: "var(--primary)", fontWeight: 600, fontSize: 15 }}>Open workspace &rarr;</Link>
                      {pending && (
                        <button onClick={() => resend(s)} data-testid={`resend-invite-card-${s._id}`} className="btn-ghost" style={{ padding: 0, fontSize: 14, color: "var(--secondary)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Send size={12} /> Resend invite
                        </button>
                      )}
                      <button
                        data-testid={`delete-seller-card-${s._id}`}
                        onClick={() => setConfirmDelete(s)}
                        className="btn-ghost"
                        style={{ padding: 0, fontSize: 14, color: "var(--error, #B85C47)", display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {showAdd && <AddSellerModal onClose={() => setShowAdd(false)} onCreated={onCreated} />}
      {confirmDelete && (
        <DeleteSellerModal
          seller={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => doDelete(confirmDelete._id)}
        />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, color, label, count, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="lifecycle-filter-chip"
      style={{
        appearance: "none",
        background: active ? color : "transparent",
        color: active ? "#fff" : "var(--text-muted)",
        border: `1px solid ${active ? color : "var(--border)"}`,
        borderRadius: 999,
        padding: "5px 12px",
        fontSize: 13,
        fontWeight: 500,
        fontFamily: "inherit",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        transition: "all 0.15s ease",
      }}
    >
      <span>{label}</span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          padding: "1px 7px",
          borderRadius: 999,
          background: active ? "rgba(255,255,255,0.22)" : "var(--surface-alt)",
          color: active ? "#fff" : "var(--text-muted)",
        }}
      >
        {count}
      </span>
    </button>
  );
}


function LifecyclePicker({ sellerId, value, onChange, compact = false }) {
  const cur = lifecycleLabel(value);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <select
        data-testid={`lifecycle-select-${sellerId}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Set lifecycle status"
        className="lifecycle-pill"
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          background: cur.color,
          color: "#fff",
          border: "none",
          borderRadius: 999,
          padding: compact ? "3px 22px 3px 10px" : "4px 26px 4px 12px",
          fontSize: compact ? 11 : 12,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          cursor: "pointer",
          fontFamily: "inherit",
          lineHeight: 1.4,
        }}
      >
        {LIFECYCLE.map((l) => (
          <option key={l.id} value={l.id} style={{ color: "#000" }}>{l.label}</option>
        ))}
      </select>
      <ChevronDown
        size={compact ? 11 : 13}
        color="#fff"
        style={{ position: "absolute", right: compact ? 6 : 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
      />
    </div>
  );
}


function AddSellerModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const buyerFeaturesEnabled = !!user?.buyer_features_enabled;
  const [form, setForm] = useState({ name: "", email: "", property_address: "", send_email: true, co_owner_email: "", co_owner_name: "", transaction_type: "sell" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [gate, setGate] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setGate(""); setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.co_owner_email) { delete payload.co_owner_email; delete payload.co_owner_name; }
      const { data } = await api.post("/admin/sellers", payload);
      setResult(data);
    } catch (e2) {
      // 402 is the first-listing-free gate, not a form error. It gets an
      // explanation and a way forward, not a red line under a field.
      if (e2.response?.status === 402) {
        setGate(formatApiError(e2.response?.data?.detail)
          || "Start your subscription to add more clients.");
      } else {
        setErr(formatApiError(e2.response?.data?.detail) || e2.message);
      }
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result.invite_link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.5)", zIndex: 50 }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(560px, 92vw)", background: "var(--bg)", borderRadius: 16, zIndex: 51, padding: 32, boxShadow: "0 40px 80px rgba(0,0,0,0.2)", maxHeight: "92vh", overflow: "auto" }} data-testid="add-seller-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <span className="overline">New client</span>
            <h2 className="font-serif" style={{ fontSize: 28, fontWeight: 500, margin: "6px 0 0", letterSpacing: "-0.01em" }}>
              {result ? "Invite sent!" : "Invite a client"}
            </h2>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="close-add-modal"><X size={18} /></button>
        </div>

        {!result ? (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
              We&rsquo;ll create a pending account and send a welcome email with an activation link. If two people are going through this together (spouses, partners), add the second person below &mdash; they&rsquo;ll both share one set of tasks, dates, and messages.
            </p>

            {/* Transaction-type picker — determines which portal(s) this
                client sees on login. "Both" gives them a nav toggle.
                Hidden entirely for tenants not in the buyer beta. */}
            {buyerFeaturesEnabled && (
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 8 }}>Client type</label>
              <div role="radiogroup" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {[
                  { value: "sell", label: "Seller",  desc: "Listing a home" },
                  { value: "buy",  label: "Buyer",   desc: "Purchasing a home" },
                  { value: "both", label: "Both",    desc: "Buying & selling" },
                ].map((opt) => {
                  const active = form.transaction_type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      data-testid={`new-seller-type-${opt.value}`}
                      onClick={() => setForm({ ...form, transaction_type: opt.value })}
                      style={{
                        textAlign: "left", cursor: "pointer",
                        padding: "12px 14px", borderRadius: 10,
                        border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
                        background: active ? "var(--surface-alt)" : "#fff",
                        boxShadow: active ? "0 0 0 2px rgba(43,76,63,0.12)" : "none",
                        transition: "all 120ms ease",
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: active ? "var(--primary)" : "var(--text)" }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Full name</label>
              <input data-testid="new-seller-name" className="input-field" required value={form.name} onChange={upd("name")} placeholder="Jane Seller" />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Email</label>
              <input data-testid="new-seller-email" type="email" className="input-field" required value={form.email} onChange={upd("email")} placeholder="jane@email.com" />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Property address <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span></label>
              <input data-testid="new-seller-address" className="input-field" value={form.property_address} onChange={upd("property_address")} placeholder="123 Maple Lane, City ST" />
            </div>

            <div className="card" style={{ padding: 16, background: "var(--surface-alt)", border: "1px solid var(--border)" }}>
              <div className="overline" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Users size={11} /> Co-client (optional)
              </div>
              <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "6px 0 12px" }}>
                Two people going through this together? Add the second person here. Both get their own login but share everything.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Co-client name</label>
                  <input data-testid="co-owner-name" className="input-field" value={form.co_owner_name} onChange={upd("co_owner_name")} placeholder="John Doe" />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Co-client email</label>
                  <input data-testid="co-owner-email" type="email" className="input-field" value={form.co_owner_email} onChange={upd("co_owner_email")} placeholder="john@email.com" />
                </div>
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--text-muted)", marginTop: 4 }}>
              <input data-testid="new-seller-send-email" type="checkbox" checked={form.send_email} onChange={upd("send_email")} style={{ accentColor: "var(--primary)" }} />
              Send welcome email{form.co_owner_email ? "s" : ""} now
            </label>
            {gate && (
              <div data-testid="new-seller-paywall"
                   style={{ border: "1px solid var(--border)", borderRadius: 10,
                            padding: 14, fontSize: 14, lineHeight: 1.5 }}>
                <strong>Your first listing is free, and this is your second.</strong>
                <div style={{ color: "var(--text-muted)", marginTop: 6 }}>{gate}</div>
                <a href="/admin/billing"
                   style={{ display: "inline-block", marginTop: 10, fontWeight: 600 }}>
                  See plans
                </a>
              </div>
            )}
            {err && <div data-testid="new-seller-error" style={{ color: "var(--error)", fontSize: 14 }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
              <button data-testid="new-seller-submit" type="submit" className="btn-primary" disabled={busy}>
                {busy ? "Creating…" : "Create & invite"}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 14 }}>
              {result.email?.sent
                ? <><b style={{ color: "var(--text)" }}>Welcome email sent to {result.seller.email}.</b> They&rsquo;ll click the link to set their password.</>
                : <><b style={{ color: "var(--text)" }}>Account created and invite link ready.</b> {result.email?.reason && result.email.reason.toLowerCase().includes("example.com") ? "(Skipped the auto-email because example.com is a Resend test domain — copy the link below to send it manually.)" : "Copy the link below and send it to your client however you like."}</>
              }
              {result.co_owner && <><br/><b style={{ color: "var(--text)" }}>Co-client invite also created.</b> They&rsquo;ll receive their own activation link.</>}
            </p>
            <div className="card" style={{ padding: 16, marginTop: 16, background: "var(--surface-alt)", border: "1px solid var(--border)" }} data-testid="invite-link-card">
              <div style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Primary invite link</div>
              <div style={{ fontSize: 13, color: "var(--text)", wordBreak: "break-all", marginTop: 6, fontFamily: "monospace" }}>{result.invite_link}</div>
              <button onClick={copy} className="btn-secondary" style={{ marginTop: 12, padding: "6px 12px", fontSize: 13 }} data-testid="copy-invite-link">
                {copied ? (<><Check size={12} /> Copied!</>) : (<><Copy size={12} /> Copy link</>)}
              </button>
            </div>
            {result.co_owner?.invite_link && (
              <div className="card" style={{ padding: 16, marginTop: 12, background: "var(--surface-alt)", border: "1px solid var(--border)" }} data-testid="co-owner-link-card">
                <div style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Co-client invite link</div>
                <div style={{ fontSize: 13, color: "var(--text)", wordBreak: "break-all", marginTop: 6, fontFamily: "monospace" }}>{result.co_owner.invite_link}</div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button className="btn-secondary" onClick={onClose} data-testid="done-btn">Done</button>
              <a href={`/admin/seller/${result.seller._id}`} className="btn-primary" data-testid="personalize-now-btn">
                Open workspace →
              </a>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function DeleteSellerModal({ seller, onClose, onConfirm }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = typed.trim().toUpperCase() === "DELETE";
  const co = (seller.co_owners || [])[0];

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.55)", zIndex: 50 }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(440px, 92vw)", background: "var(--bg)", borderRadius: 16, zIndex: 51, padding: 28, boxShadow: "0 40px 80px rgba(0,0,0,0.2)" }} data-testid="delete-seller-modal">
        <span className="overline" style={{ color: "var(--error, #B85C47)" }}>Permanent action</span>
        <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: "6px 0 12px" }}>Delete {seller.name}?</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.5 }}>
          {co
            ? <>This removes <b style={{ color: "var(--text)" }}>{seller.email}</b>&rsquo;s login. Their co-client <b style={{ color: "var(--text)" }}>{co.name || co.email}</b> still has access to the household, including all submissions, prep tasks, and messages.</>
            : <>This permanently removes <b style={{ color: "var(--text)" }}>{seller.email}</b> AND all their submissions, prep progress, custom tasks, and messages. This cannot be undone.</>
          }
        </p>
        <div style={{ marginTop: 18 }}>
          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Type <code style={{ background: "var(--surface-alt)", padding: "2px 6px", borderRadius: 4 }}>DELETE</code> to confirm</label>
          <input
            data-testid="delete-confirm-input"
            className="input-field"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="DELETE"
            autoFocus
          />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            data-testid="delete-confirm-btn"
            className="btn-primary"
            disabled={!ok || busy}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}
            style={{ background: "var(--error, #B85C47)" }}
          >
            <Trash2 size={14} /> {busy ? "Deleting…" : "Delete seller"}
          </button>
        </div>
      </div>
    </>
  );
}
